/**
 * Navigation Bar Component
 * Displays user information and navigation links with mobile burger menu
 */

import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

function Navbar({ user }) {
    const location = useLocation();
    const isAdmin = user?.role === 'admin';
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    if (!user) return null;

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const closeMenu = () => {
        setIsMenuOpen(false);
    };

    return (
        <nav className="bg-white shadow-md border-b border-gray-200 sticky top-0 z-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-16">
                    {/* Logo/Title */}
                    <div className="flex items-center">
                        <Link to="/" className="text-xl font-bold text-gray-900 hover:text-blue-600 transition-colors" onClick={closeMenu}>
                            بوت الشرطة
                        </Link>
                    </div>

                    {/* Desktop Navigation - Hidden on mobile */}
                    <div className="hidden md:flex items-center space-x-6 space-x-reverse">
                        {/* Public Links */}
                        <Link
                            to="/"
                            className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                location.pathname === '/'
                                    ? 'bg-blue-100 text-blue-700'
                                    : 'text-gray-700 hover:bg-gray-100'
                            }`}
                        >
                            الرئيسية
                        </Link>

                        {/* Admin Only Links */}
                        {isAdmin && (
                            <>
                                <Link
                                    to="/admin"
                                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                        location.pathname === '/admin'
                                            ? 'bg-blue-100 text-blue-700'
                                            : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                                >
                                    لوحة التحكم
                                </Link>
                                <Link
                                    to="/admin/teams"
                                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                        location.pathname === '/admin/teams'
                                            ? 'bg-blue-100 text-blue-700'
                                            : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                                >
                                    الفرق
                                </Link>
                                <Link
                                    to="/admin/info"
                                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                        location.pathname === '/admin/info'
                                            ? 'bg-blue-100 text-blue-700'
                                            : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                                >
                                    معلومات المدير
                                </Link>
                            </>
                        )}

                        {/* User Info - Desktop */}
                        <div className="flex items-center space-x-2 space-x-reverse mr-4 border-r border-gray-300 pr-4">
                            <div className="text-right">
                                <p className="text-sm font-semibold text-gray-900">
                                    {user.firstName} {user.lastName || ''}
                                </p>
                                <p className="text-xs text-gray-500">
                                    {user.username ? `@${user.username}` : `ID: ${user.userId}`}
                                </p>
                            </div>
                            <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                user.role === 'admin' 
                                    ? 'bg-blue-500 text-white' 
                                    : user.role === 'reporter'
                                    ? 'bg-purple-500 text-white'
                                    : 'bg-gray-500 text-white'
                            }`}>
                                {user.role === 'admin' ? 'مدير' : user.role === 'reporter' ? 'مراسل' : 'مشاهد'}
                            </div>
                        </div>
                    </div>

                    {/* Mobile: User Info + Burger Menu */}
                    <div className="flex md:hidden items-center space-x-3 space-x-reverse">
                        {/* User Badge - Mobile */}
                        <div className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            user.role === 'admin' 
                                ? 'bg-blue-500 text-white' 
                                : user.role === 'reporter'
                                ? 'bg-purple-500 text-white'
                                : 'bg-gray-500 text-white'
                        }`}>
                            {user.role === 'admin' ? 'مدير' : user.role === 'reporter' ? 'مراسل' : 'مشاهد'}
                        </div>

                        {/* Burger Menu Button */}
                        <button
                            onClick={toggleMenu}
                            className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                            aria-expanded="false"
                        >
                            <span className="sr-only">افتح القائمة</span>
                            {!isMenuOpen ? (
                                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                </svg>
                            ) : (
                                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            )}
                        </button>
                    </div>
                </div>

                {/* Mobile Menu - Dropdown */}
                {isMenuOpen && (
                    <div className="md:hidden border-t border-gray-200">
                        <div className="px-2 pt-2 pb-3 space-y-1">
                            {/* User Info - Mobile */}
                            <div className="px-3 py-2 mb-3 bg-gray-50 rounded-lg">
                                <p className="text-sm font-semibold text-gray-900">
                                    {user.firstName} {user.lastName || ''}
                                </p>
                                <p className="text-xs text-gray-500">
                                    {user.username ? `@${user.username}` : `ID: ${user.userId}`}
                                </p>
                            </div>

                            {/* Navigation Links - Mobile */}
                            <Link
                                to="/"
                                onClick={closeMenu}
                                className={`block px-3 py-2 rounded-md text-base font-medium ${
                                    location.pathname === '/'
                                        ? 'bg-blue-100 text-blue-700'
                                        : 'text-gray-700 hover:bg-gray-100'
                                }`}
                            >
                                الرئيسية
                            </Link>

                            {/* Admin Only Links - Mobile */}
                            {isAdmin && (
                                <>
                                    <Link
                                        to="/admin"
                                        onClick={closeMenu}
                                        className={`block px-3 py-2 rounded-md text-base font-medium ${
                                            location.pathname === '/admin'
                                                ? 'bg-blue-100 text-blue-700'
                                                : 'text-gray-700 hover:bg-gray-100'
                                        }`}
                                    >
                                        لوحة التحكم
                                    </Link>
                                    <Link
                                        to="/admin/teams"
                                        onClick={closeMenu}
                                        className={`block px-3 py-2 rounded-md text-base font-medium ${
                                            location.pathname === '/admin/teams'
                                                ? 'bg-blue-100 text-blue-700'
                                                : 'text-gray-700 hover:bg-gray-100'
                                        }`}
                                    >
                                        الفرق
                                    </Link>
                                    <Link
                                        to="/admin/info"
                                        onClick={closeMenu}
                                        className={`block px-3 py-2 rounded-md text-base font-medium ${
                                            location.pathname === '/admin/info'
                                                ? 'bg-blue-100 text-blue-700'
                                                : 'text-gray-700 hover:bg-gray-100'
                                        }`}
                                    >
                                        معلومات المدير
                                    </Link>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </nav>
    );
}

export default Navbar;
